import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { extractErrors } from "src/pages/helpers";
import { getInhouseAnonymousId } from "src/utils/anonymousId";
import axios from "axios";
import { toast } from "react-toastify";

import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { getUserMetadata } from "src/store/slices/onboardingSlice";
import { isAlreadySubscribedDetail } from "src/utils/paymentErrors";
// A 200 from process-card-payment does NOT mean the charge settled. Only a
// settled charge triggers the metadata refresh below; paymentStatus.ts owns the
// rule for this gate and for CheckoutSlide's three submit paths.
import { isSettledCardPayment } from "src/utils/paymentStatus";

const BASE_ENDPOINT = process.env.REACT_APP_BACKEND_BASE_ENDPOINT;

const nonEmptyString = (value) => (typeof value === "string" && value.trim() ? value : null);

export const validateCoupon = createAsyncThunk("chat/validateCoupon", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/validate-coupon`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, data, { headers: headers });
    if (!response.data.valid) {
      // The BE explains an ineligible promo (e.g. "The free trial is for new
      // members only.") in `message`; the reducer surfaces it verbatim.
      const message = response.data.message ?? null;
      return thunkAPI.rejectWithValue({ invalid: true, message, refusalReason: nonEmptyString(message) });
    }
    return { response: response.data };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    // Only a 400 with a string detail is the server refusing the CODE (e.g.
    // "This code only applies to a plan on its own."). A 401/404/422 detail
    // describes the request, not the code, and must not block checkout.
    const refusalReason = error?.response?.status === 400 ? nonEmptyString(detail) : null;
    return thunkAPI.rejectWithValue({ error: error, message: detail || null, refusalReason });
  }
});

export const getPaymentStatus = createAsyncThunk("chat/getPaymentStatus", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/check-payment-status/${data.payment_id}`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.get(url, { headers: headers });
    return {
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

// Fast status check that reads directly from database (no Chargebee API calls)
// Use this for custom checkout flow after process-card-payment
export const checkDirectPaymentStatus = createAsyncThunk("chat/checkDirectPaymentStatus", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/check-direct-payment-status/${data.payment_id}`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.get(url, { headers: headers });
    return {
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

// ─── Duplicate-subscription (already_subscribed) handling ────────────────────
// Both get-checkout-url and process-card-payment can return HTTP 409
// { detail: { error_code: "already_subscribed" } } once the backend re-checks
// Chargebee (source of truth) before creating a subscription. This means the
// user already has a LIVE subscription — it is NOT a payment failure and must
// NOT be conflated with the normal 200 { status: "failed" } Chargebee decline.
// Both endpoints route through the same handler so the UX is identical.
// (Requested by Backend team, 2026-07-07 — signup double-charge fix.)
//
// This handler sees the RAW axios error (error.response.data.detail). The match
// rule itself lives in src/utils/paymentErrors so checkoutFlowSlice — which sees
// the { error: axiosError } rejectWithValue wrapper — can share it without the
// two copies drifting apart. (Centralized per PR #1395 review, 2026-07-07.)
export const isAlreadySubscribedError = (error) =>
  isAlreadySubscribedDetail(error?.response?.data?.detail);

const notifyAlreadySubscribed = (thunkAPI) => {
  toast.info("You're already subscribed to InHouse Pro.", {
    toastId: "already-subscribed",
    autoClose: 5000,
  });
  thunkAPI.dispatch(getUserMetadata());
};

export const getCheckoutUrl = createAsyncThunk("chat/getCheckoutUrl", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/get-checkout-url`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, data, { headers: headers });
    return {
      response: response.data,
    };
  } catch (error) {
    // 402 subscription_required / consultation_requires_counsel now route through
    // the central axios interceptor in src/services/apiClient.ts → CheckoutFlowModal.
    // Keep the already-subscribed toast: it surfaces a 409 mismatch that the
    // interceptor doesn't own, and the silent failure used to leave the user
    // staring at a CTA that "did nothing".
    if (isAlreadySubscribedError(error)) {
      notifyAlreadySubscribed(thunkAPI);
    }
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const processCardPayment = createAsyncThunk("chat/processCardPayment", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/process-card-payment`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, data, { headers: headers });
    // Refresh get-metadata once the charge settles. The BE mutates user state as
    // a side effect of the charge (subscription_type, credits, consultation lead),
    // so the store is stale the moment this resolves. SuccessSlide polls metadata
    // for SUBSCRIPTION targets, but a one-time target starts `tierFlipped: true`
    // and never polls — this is the only refresh that path gets.
    // (Requested by Pulkit, 2026-08-26 — self / internal.)
    if (isSettledCardPayment(response.data)) {
      thunkAPI.dispatch(getUserMetadata());
    }
    return { response: response.data };
  } catch (error) {
    // A thrown 409 already_subscribed is NOT a payment failure — the backend
    // re-checked Chargebee and found a live subscription. Route it through the
    // same handler as get-checkout-url (toast + metadata refresh). The normal
    // 200 { status: "failed" } decline never reaches this catch.
    if (isAlreadySubscribedError(error)) {
      notifyAlreadySubscribed(thunkAPI);
    }
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const refreshPaymentIntent = createAsyncThunk("chat/refreshPaymentIntent", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/refresh-payment-intent`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, data, { headers });
    return { response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error });
  }
});

export const updateCheckout = createAsyncThunk("chat/updateCheckout", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/update-checkout`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, data, { headers });
    return { response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error });
  }
});

export const getUserPayments = createAsyncThunk("chat/getUserPayments", async (_, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/payments`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.get(url, { headers });
    return { response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error });
  }
});

export const refreshPaymentStatus = createAsyncThunk("chat/refreshPaymentStatus", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/payments/${data.payment_id}/refresh-status`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, {}, { headers });
    return { response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const consumePayment = createAsyncThunk("chat/consumePayment", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/consume-payment`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, data, { headers });
    return { response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const getBillingDetails = createAsyncThunk("chat/getBillingDetails", async (_, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/billing-details`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.get(url, { headers: headers });
    return { response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const getPaymentSources = createAsyncThunk("chat/getPaymentSources", async (_, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/payment-sources`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.get(url, { headers: headers });
    return { response: response.data };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const upgradeProUserToCounsel = createAsyncThunk("chat/upgradeProUserToCounsel", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const url = `${BASE_ENDPOINT}/api/user/update-subscription`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const response = await axios.post(url, data, { headers: headers });
    return {
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const submitLegalReview = createAsyncThunk("chat/submitLegalReview", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  const url = `${BASE_ENDPOINT}/api/v0/thread/${data.thread_id}/submit-legal-review`;
  try {
    const response = await axios.post(url, {}, { headers: headers });
    return {
      response: response.data,
      request_data: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const sendFeedback = createAsyncThunk("chat/sendFeedback", async (data, thunkAPI) => {
  let headers;

  if (window.location.pathname.includes("/try")) {
    const anonymousId = await getInhouseAnonymousId();
    headers = { "anonymous-user-id": anonymousId };
  } else {
    const accessToken = localStorage.getItem("accessToken2");
    headers = { Authorization: `Bearer ${accessToken}` };
  }

  try {
    const url = `${BASE_ENDPOINT}/api/message/${data.message_id}/add-user-feedback`;
    const response = await axios.post(url, data, { headers: headers });
    return {
      response: response.data,
      data: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const sendFeedbackWithMessage = createAsyncThunk("chat/sendFeedbackWithMessage", async (data, thunkAPI) => {
  let headers;
  if (window.location.pathname.includes("/try")) {
    const anonymousId = await getInhouseAnonymousId();
    headers = { "anonymous-user-id": anonymousId };
  } else {
    const accessToken = localStorage.getItem("accessToken2");
    headers = { Authorization: `Bearer ${accessToken}` };
  }

  try {
    const url = `${BASE_ENDPOINT}/api/message/${data.message_id}/add-user-feedback`;
    const response = await axios.post(url, data, { headers: headers });
    return {
      response: response.data,
      data: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const addLegalReviewFeedback = createAsyncThunk("chat/addLegalReviewFeedback", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const url = `${BASE_ENDPOINT}/api/v0/thread/${data.threadId}/add-legal-review-feedback`;
    const response = await axios.post(url, { feedback: data.rating }, { headers: headers });
    return {
      response: response.data,
      data: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});


export const convertDocument = createAsyncThunk("chat/convertDocument", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const url = `${BASE_ENDPOINT}/api/v0/user/document-to-text`;
    const response = await axios.post(url, data, { headers: headers });
    return {
      response: response,
      index: data[1],
      // "title": data[0]
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const acceptEditSuggestions = createAsyncThunk("chat/acceptEditSuggestions", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  try {
    const url = `${BASE_ENDPOINT}/api/v0/thread/${data.thread_id}/accept-edit-suggestions`;
    const response = await axios.post(url, data, { headers: headers });
    return {
      response: response.data,
      request: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

export const dismissPlaceholderAPI = createAsyncThunk("chat/dismissPlaceholderAPI", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  try {
    const url = `${BASE_ENDPOINT}/api/v0/thread/${data}/dismiss-placeholder`;
    const response = await axios.post(url, {}, { headers: headers });
    return {
      response: response.data,
      request: data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error });
  }
});

const chatSlice = createSlice({
  name: "chat",
  initialState: {
    showPaymentStatusCheckModal: false,
    paymentStatusFetchStatus: "idle",
    transactionType: null,
    paymentStatus: "",
    messages: [],
    viewedChats: [],
    status: "idle",
    error: null,
    newChat: "new",
    threadId: null,
    nextStep: null,
    threads: [],
    lastMessageId: null,
    sendMessageStatus: "idle",
    sendMessageError: "",
    sendMessageLawyerStatus: "idle",
    sendMessageLawyerError: "",
    feedbackStatus: "idle",
    getThreadStatus: "idle",
    deletedThread: {},
    deleteThreadStatus: "idle",
    updateTitleStatus: "idle",
    updateTitleError: null,
    updatedThreadId: null,
    feedbackError: null,
    deleteThreadError: null,
    // messagingDisabled: false,
    submitLegalReviewStatus: "idle",
    submitLegalReviewError: null,
    legalReviewSubmitted: false,
    legalReviewRequested: false,
    textExtractionStatus: "idle",
    textExtractionError: null,
    textExtractionTrimmed: null,
    extractedText: "",
    acceptLegalReviewStatus: "idle",
    acceptLegalReviewError: null,
    rejectLegalReviewStatus: "idle",
    rejectLegalReviewError: null,
    selectedThread: null,
    intent_state: "ask",
    changesAccepted: false,
    lastConversationalMessage: null,
    acceptingEditSuggestionsStatus: "idle",
    userMessageCount: 0,
    saveAsTemplateChats: [],
    viewChatLinks: [],
    legalReviewRequestedChats: [],
    activePlaceholder: null,
    threadData: {},
    openChargebeePaymentModal: false,
    openPaymentSuccessModal: false,
    checkoutUrl: null,
    checkoutUrlStatus: "idle",
    paymentLinkExpired: false,
    expiredPaymentId: null,
    paymentReceived: null,
    processCardPaymentStatus: "idle",
    // process-card-payment tells us whether the charge opened a free trial
    // (INH-5478); SuccessSlide reads these for its toast copy.
    isTrial: false,
    trialEnd: null,
    upgradeProUserToCounselStatus: "idle",
    selectedPaymentPlanName: null,
    paymentCancellationScreen: { action: "", plans: {} },
    preparingCheckout: false,
    coupon: {
      status: "idle",
      validated: false,
      coupon_id: null,
      name: null,
      discount_type: null,
      discount_percentage: null,
      discount_amount: null,
      currency_code: null,
      trial_days: null,
      trial_end: null,
      error: null,
      refusal_reason: null,
      refused_coupon_id: null,
    },
    billingDetails: null,
    billingDetailsStatus: "idle",
    paymentSources: [],
    paymentSourcesStatus: "idle",
    userPayments: [],
    userPaymentsStatus: "idle",
  },
  reducers: {
    setSelectedPaymentPlanName: (state, action) => {
      state.selectedPaymentPlanName = action.payload;
    },
    setPaymentCancellationScreen: (state, action) => {
      state.paymentCancellationScreen = action.payload;
    },
    closeChargebeePaymentModal: (state) => {
      state.openChargebeePaymentModal = false;
    },
    openChargebeePaymentModal: (state) => {
      state.openChargebeePaymentModal = true;
    },
    setUserSelectedPlan: (state, action) => {
      state.userSelectedPlan = action.payload;
    },
    changePaymentSuccessModalView: (state, action) => {
      state.openPaymentSuccessModal = action.payload;
    },

    setShowPaymentStatusCheckModal: (state, action) => {
      state.showPaymentStatusCheckModal = action.payload;
    },

    setPlaceholderDismissed: (state, action) => {
      if (state.threadData && state.threadData.placeholders) {
        var skippedPlaceholders = state.threadData.placeholders.skippedPlaceholders || [];
        if (skippedPlaceholders.indexOf(action.payload) === -1) {
          skippedPlaceholders.push(action.payload);
        }
        state.threadData.placeholders.skippedPlaceholders = skippedPlaceholders;
      } else if (!state.threadData) {
        state.threadData = { placeholders: { skippedPlaceholders: [action.payload] } };
      } else if (!state.threadData.placeholders) {
        state.threadData.placeholders = { skippedPlaceholders: [action.payload] };
      }
      var placeholdersLength = state.threadData.placeholders.placeholders.length;
      if (action.payload === placeholdersLength - 1) {
        state.activePlaceholder = null;
        state.threadData.placeholders.activePlaceholder = null;
      } else {
        state.threadData.placeholders.activePlaceholder = action.payload + 1;
      }
    },
    resetPaymentStatus: (state) => {
      state.checkoutUrlStatus = "idle";
      state.paymentStatus = "";

      state.checkoutUrl = null;
      state.transactionType = null;
      state.paymentStatusFetchStatus = "idle";
      state.paymentLinkExpired = false;
      state.expiredPaymentId = null;
      state.paymentReceived = null;
    },
    setPaymentLinkExpired: (state, action) => {
      state.paymentLinkExpired = true;
      state.expiredPaymentId = action.payload.paymentId;
    },
    setPaymentReceived: (state, action) => {
      state.paymentReceived = action.payload;
    },
    setActivePlaceholder: (state, action) => {
      state.activePlaceholder = action.payload;
    },
    resetChatPage: (state, action) => {
      state.messages = [];
      state.textExtractionTrimmed = null;
      if (!action.payload || !action.payload.newThreadCreated) {
        state.status = "idle";
        state.sendMessageStatus = "idle";
        state.newThreadCreated = false;
        state.lastMessageId = null;
        state.getThreadStatus = "idle";
        // state.messagingDisabled = false;
        state.messageSentAt = null;
        state.messageSentForThread = null;
      }
      state.additionalMessagesFetchedStatus = "idle";
      state.saveAsTemplateChats = [];
      state.viewChatLinks = [];
      state.legalReviewRequestedChats = [];
      state.error = null;
      state.newChat = "new";
      state.threadId = null;
      state.nextStep = null;
      state.feedbackStatus = "idle";
      state.deleteThreadStatus = "idle";
      state.deletedThread = null;
      state.getDynamicQuestionsStatus = "idle";
      state.sendMessageError = "";
      state.sendMessageLawyerStatus = "idle";
      state.sendMessageLawyerError = "";
      state.getAllThreadStatus = "idle";
      state.feedbackError = null;
      state.legalReviewSubmitted = false;
      state.legalReviewRequested = false;
      state.legalReviewAccepted = false;
      state.getThreadError = null;
      state.getThreadsError = null;
      state.deleteThreadError = null;
      state.getDynamicQuestionsError = null;
      state.getAllThreadError = null;
      state.legalReviewRequested = false;
      state.acceptLegalReviewStatus = "idle";
      state.acceptLegalReviewError = null;
      state.rejectLegalReviewStatus = "idle";
      state.rejectLegalReviewError = null;
      state.legalReviewFedbackStatus = "idle";
      state.legalReviewFeedbackError = null;
      state.intent_state = "ask";
      // state.templateList = [];
      // state.templateListStatus = "idel";
      // state.templateListError = null;
      state.templateRenameStatus = "idle";
      state.templateRenameError = null;
      state.templateDeleteStatus = "idle";
      state.templateDeleteError = null;
      state.templateSaved = null;
      state.templateSaveStatus = "idle";
      state.templateSaveError = null;
      state.externalTemplateSaveError = null;
      state.externalTemplateSaveStatus = "idle";
      state.externalTemplateSaved = null;
      state.lastConversationalMessage = null;
      state.userMessageCount = 0;
    },
    updateUserMessageCount: (state, action) => {
      state.userMessageCount = action.payload;
    },
    updateLegalReviewRequestChats: (state, action) => {
      if (state.legalReviewRequestedChats.includes(action.payload)) {
        return;
      }
      state.legalReviewRequestedChats = [...state.legalReviewRequestedChats, ...action.payload];
    },
    updateViewChatLinks: (state, action) => {
      if (state.viewChatLinks.includes(action.payload)) {
        return;
      }
      state.viewChatLinks = [...state.viewChatLinks, ...action.payload];
    },
    setLastConversationalMessage: (state, action) => {
      state.lastConversationalMessage = action.payload;
    },
    resetCoupon: (state) => {
      state.coupon = {
        status: "idle",
        validated: false,
        coupon_id: null,
        name: null,
        discount_type: null,
        discount_percentage: null,
        discount_amount: null,
        currency_code: null,
        trial_days: null,
        trial_end: null,
        error: null,
        refusal_reason: null,
        refused_coupon_id: null,
      };
    },
    setPreparingCheckout: (state, action) => {
      state.preparingCheckout = action.payload;
    },
    setCheckoutUrl: (state, action) => {
      state.checkoutUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // sendFeedback handlers
      .addCase(sendFeedbackWithMessage.pending, (state) => {
        state.feedbackStatus = "loading";
        state.error = "";
      })
      .addCase(sendFeedbackWithMessage.fulfilled, (state, action) => {
        if (action.payload.data.feedback !== "") {
          state.feedbackStatus = "success";
        } else {
          state.feedbackStatus = "idle";
        }
        state.error = "";
        state.threadId = action.payload.thread_id;
        state.nextStep = action.payload.next_step;
      })
      .addCase(sendFeedbackWithMessage.rejected, (state, action) => {
        // if (action.payload.error?.response?.status === 401) {
        //   attemptAuthenticationRefresh();
        // }
        state.feedbackStatus = "failed";
        state.error = action.payload.error;
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "failed_to_send_feedback");
        }
      })

      .addCase(submitLegalReview.pending, (state) => {
        state.submitLegalReviewStatus = "loading";
        state.submitLegalReviewError = "";
      })
      .addCase(submitLegalReview.fulfilled, (state, action) => {
        let message = action.payload.response.message;
        state.messages = [...state.messages, message];
        state.submitLegalReviewStatus = "success";
        state.submitLegalReviewError = "";
        state.legalReviewRequested = action?.payload?.response?.legal_review_requested;
        state.legalReviewSubmitted = action?.payload?.response?.legal_review_submitted;
        state.legalReviewAccepted = action?.payload?.response?.legal_review_accepted;
      })
      .addCase(submitLegalReview.rejected, (state, action) => {
        // if (action.payload.error?.response?.status === 401) {
        //   attemptAuthenticationRefresh();
        // }
        state.submitLegalReviewStatus = "failed";
        state.submitLegalReviewError = action.payload.error;
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "submit_legal_review_error");
        }
      })
      // document to text conversion
      .addCase(convertDocument.pending, (state) => {
        state.textExtractionStatus = "loading";
        state.textExtractionError = "";
        state.textExtractionTrimmed = null;
      })
      .addCase(convertDocument.fulfilled, (state, action) => {
        state.textExtractionStatus = "success";
        state.textExtractionError = "";
        state.extractedText = action.payload.response.data.text;
        state.textExtractionTrimmed = action.payload.response.data.restricted_word_count;
      })
      .addCase(convertDocument.rejected, (state, action) => {
        state.textExtractionStatus = "failed";
        state.textExtractionError = action.payload.error;
        state.textExtractionTrimmed = null;
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "convert_document_error");
        }
      })

      .addCase(addLegalReviewFeedback.fulfilled, (state, action) => {
        state.legalReviewFedbackStatus = "success";
        state.legalReviewFedbackError = "";
        state.legalReviewFedback = action.payload.data;
      })
      .addCase(addLegalReviewFeedback.rejected, (state, action) => {
        state.legalReviewFedbackStatus = "failed";
        state.legalReviewFedbackError = extractErrors(action.payload.error);
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "add_legal_review_feedback_error");
        }
      })
      .addCase(addLegalReviewFeedback.pending, (state, action) => {
        state.feedbackStatus = "loading";
        state.feedbackError = "";
      })

      .addCase(acceptEditSuggestions.fulfilled, (state, action) => {
        state.acceptingEditSuggestionsStatus = "success";
        state.changesAccepted = true;
        state.messages = [...state.messages, action.payload.response];
      })
      .addCase(acceptEditSuggestions.rejected, (state, action) => {
        state.changesAccepted = false;
        state.acceptingEditSuggestionsStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "");
        }
      })
      .addCase(acceptEditSuggestions.pending, (state, action) => {
        state.changesAccepted = false;
        state.acceptingEditSuggestionsStatus = "loading";
      })
      .addCase(getCheckoutUrl.pending, (state, action) => {
        // state.checkoutUrl = action.payload.response.url;
        state.checkoutUrlStatus = "loading";
      })
      .addCase(getCheckoutUrl.fulfilled, (state, action) => {
        state.checkoutUrl = action.payload.response;
        state.checkoutUrlStatus = "success";
        // Navigation to checkout page is handled by the caller
        // state.checkoutUrl = {
        //   "url": "https://inhouse-test.chargebee.com/pages/v4/nVAwWSvbw9QpHcpG5RwG8yCptNPlrFVU/?signature=VSRBcdk8cXM3Ce1URa9FcuzxkCSBEhJaBJ",
        //   "url_expiry_time": 1730984701,
        //   "payment_id": "4d9a65e3-c7f5-42c7-900f-1d8f69d7a15a"
        // }
      })
      .addCase(getCheckoutUrl.rejected, (state, action) => {
        const httpStatus = action.payload?.error?.response?.status;
        const errorDetail = action.payload?.error?.response?.data?.detail;
        if (httpStatus === 409 && errorDetail?.error === "PaymentInProgress" && errorDetail?.payment_id) {
          // A payment for this item is already being processed — surface the existing payment_id
          state.checkoutUrl = "";
          state.checkoutUrlStatus = "payment_in_progress";
          state.expiredPaymentId = errorDetail.payment_id;
          return;
        }
        var message = "Failed to get checkout url";
        try {
          message = JSON.parse(action.payload.error.request.response).detail;
          if (message.toLowerCase().search("user is already ") > -1) {
            message = "You are already subscribed to this plan";
          }
        } catch (e) {}
        state.checkoutUrl = "";
        state.checkoutUrlStatus = message;
        if (httpStatus !== 401) {
          ErrorLoggingV2(action?.payload?.error, "get_checkout_url_error");
        }
      })
      .addCase(processCardPayment.pending, (state) => {
        state.processCardPaymentStatus = "loading";
        state.paymentStatus = "";
        state.transactionType = null;
        state.isTrial = false;
        state.trialEnd = null;
      })
      .addCase(processCardPayment.fulfilled, (state, action) => {
        state.processCardPaymentStatus = action.payload.response.status;
        state.paymentStatus = action.payload.response.status;
        state.paymentStatusFetchStatus = "success";
        state.transactionType = action.payload.response.transaction_type;
        state.isTrial = action.payload.response.is_trial === true;
        state.trialEnd = action.payload.response.trial_end ?? null;
      })
      .addCase(processCardPayment.rejected, (state, action) => {
        const axiosError = action?.payload?.error;
        const httpStatus = axiosError?.response?.status;
        const responseBody = axiosError?.response?.data;
        // 409 already_subscribed: the user already has a live subscription
        // (backend re-checked Chargebee before billing). Not a payment failure —
        // mark a distinct status and skip Sentry error logging. The thunk already
        // surfaced the "already subscribed" toast + metadata refresh.
        if (httpStatus === 409 && isAlreadySubscribedError(axiosError)) {
          state.processCardPaymentStatus = "already_subscribed";
          state.paymentStatusFetchStatus = "success";
          return;
        }
        state.processCardPaymentStatus = "failed";
        state.paymentStatusFetchStatus = "failed";
        const declineCode = responseBody?.decline_code || responseBody?.error?.decline_code || "";
        // Build an enriched description so Sentry captures HTTP status and decline code
        const errDescription = `process_card_payment_error | http_status=${httpStatus} | decline_code=${declineCode} | ${axiosError?.message || ""}`;
        ErrorLoggingV2(errDescription, "process_card_payment_error");
      })
      .addCase(upgradeProUserToCounsel.pending, (state, action) => {
        state.upgradeProUserToCounselStatus = "loading";
      })
      .addCase(upgradeProUserToCounsel.fulfilled, (state, action) => {
        state.upgradeProUserToCounselStatus = "success";
      })
      .addCase(upgradeProUserToCounsel.rejected, (state, action) => {
        state.upgradeProUserToCounselStatus = "failed";
        ErrorLoggingV2(action.payload, "upgrade_pro_to_counsel_plan");
      })

      .addCase(getPaymentStatus.pending, (state, action) => {
        state.paymentStatusFetchStatus = "loading";
        state.paymentStatus = "";
        state.transactionType = null;
      })
      .addCase(getPaymentStatus.fulfilled, (state, action) => {
        state.paymentStatus = action.payload.response.status;
        state.paymentStatusFetchStatus = "success";
        state.transactionType = action.payload.response.transaction_type;
      })
      .addCase(getPaymentStatus.rejected, (state, action) => {
        state.paymentStatusFetchStatus = "failed";
        state.transactionType = null;
        ErrorLoggingV2(action?.payload?.error, "get_payment_status_error");
      })
      // checkDirectPaymentStatus uses the same state as getPaymentStatus
      .addCase(checkDirectPaymentStatus.pending, (state) => {
        state.paymentStatusFetchStatus = "loading";
      })
      .addCase(checkDirectPaymentStatus.fulfilled, (state, action) => {
        state.paymentStatus = action.payload.response.status;
        state.paymentStatusFetchStatus = "success";
        state.transactionType = action.payload.response.transaction_type;
      })
      .addCase(checkDirectPaymentStatus.rejected, (state, action) => {
        state.paymentStatusFetchStatus = "failed";
        state.transactionType = null;
        ErrorLoggingV2(action?.payload?.error, "check_direct_payment_status_error");
      })
      .addCase(validateCoupon.pending, (state) => {
        state.coupon = {
          status: "loading",
          validated: false,
          coupon_id: null,
          name: null,
          discount_type: null,
          discount_percentage: null,
          discount_amount: null,
          currency_code: null,
          trial_days: null,
          trial_end: null,
          error: null,
          refusal_reason: null,
          refused_coupon_id: null,
        };
      })
      .addCase(validateCoupon.fulfilled, (state, action) => {
        const res = action.payload.response;
        state.coupon = {
          status: "success",
          validated: true,
          coupon_id: res.coupon_id,
          name: res.name,
          discount_type: res.discount_type,
          discount_percentage: res.discount_percentage,
          discount_amount: res.discount_amount,
          currency_code: res.currency_code,
          trial_days: res.trial_days ?? null,
          trial_end: res.trial_end ?? null,
          error: null,
          refusal_reason: null,
          refused_coupon_id: null,
        };
      })
      .addCase(validateCoupon.rejected, (state, action) => {
        state.coupon = {
          status: "failed",
          validated: false,
          coupon_id: null,
          name: null,
          discount_type: null,
          discount_percentage: null,
          discount_amount: null,
          currency_code: null,
          trial_days: null,
          trial_end: null,
          error: action.payload?.message || "This promo code is not valid",
          // Kept apart from `error` (display copy): set only when the server
          // refused this code with a reason. CheckoutSlide blocks submit while the
          // promo field still holds `refused_coupon_id`.
          refusal_reason: action.payload?.refusalReason ?? null,
          refused_coupon_id: action.payload?.refusalReason ? action.meta.arg?.coupon_id?.trim() || null : null,
        };
      })
      // The refreshed intent is the system of record for the trial end date
      // (Chargebee-computed); it supersedes the estimate validate-coupon gave.
      // A null trial_end (non-trial refresh) leaves the coupon untouched.
      .addCase(refreshPaymentIntent.fulfilled, (state, action) => {
        const trialEnd = action.payload?.response?.trial_end;
        if (trialEnd) state.coupon.trial_end = trialEnd;
      })
      .addCase(getBillingDetails.pending, (state) => {
        state.billingDetailsStatus = "loading";
      })
      .addCase(getBillingDetails.fulfilled, (state, action) => {
        state.billingDetailsStatus = "success";
        state.billingDetails = action.payload.response;
      })
      .addCase(getBillingDetails.rejected, (state, action) => {
        state.billingDetailsStatus = "failed";
        state.billingDetails = null;
      })
      .addCase(getPaymentSources.pending, (state) => {
        state.paymentSourcesStatus = "loading";
      })
      .addCase(getPaymentSources.fulfilled, (state, action) => {
        state.paymentSourcesStatus = "success";
        const all = action.payload.response || [];
        state.paymentSources = all.filter(
          (s) => s.type === "card" && s.card && ["valid", "expiring"].includes(s.status),
        );
      })
      .addCase(getPaymentSources.rejected, (state) => {
        state.paymentSourcesStatus = "failed";
        state.paymentSources = [];
      })
      .addCase(getUserPayments.pending, (state) => {
        state.userPaymentsStatus = "loading";
      })
      .addCase(getUserPayments.fulfilled, (state, action) => {
        state.userPaymentsStatus = "success";
        state.userPayments = action.payload.response || [];
      })
      .addCase(getUserPayments.rejected, (state) => {
        state.userPaymentsStatus = "failed";
        state.userPayments = [];
      });
  },
});

export default chatSlice.reducer;
export const {
  resetChatPage,
  // updateMessagingDisabled,
  updateMessagingWithAiMessage,
  updateSelectedThread,
  resetChangesAccepted,
  setLastConversationalMessage,
  updateUserMessageCount,
  updateViewChatLinks,
  updateLegalReviewRequestChats,
  setMessagingSentAt,
  setActivePlaceholder,
  setPlaceholderDismissed,
  closeChargebeePaymentModal,
  openChargebeePaymentModal,
  setCheckoutUrl,
  resetPaymentStatus,
  setUserSelectedPlan,
  changePaymentSuccessModalView,
  setSelectedPaymentPlanName,
  setPaymentCancellationScreen,
  setShowPaymentStatusCheckModal,
  resetCoupon,
  setPaymentLinkExpired,
  setPaymentReceived,
  setPreparingCheckout,
} = chatSlice.actions;
